"use client";

import { useCallback, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import {
	generateNarrationScript,
	scriptTextFromResponse,
	synthesizeNarration,
} from "@/lib/narration";
import { ModeSelector } from "./ModeSelector";
import {
	STORY_THEMES,
	TONES,
	VOICES,
	VOICE_CLONE_KEY,
	type NarrationMode,
	type NarrationSubmode,
} from "./narration-data";
import { ScriptEditor } from "./ScriptEditor";
import { StoryThemeGrid } from "./StoryThemeGrid";
import { ToneGrid } from "./ToneGrid";
import { VoiceCloneWizard } from "./VoiceCloneWizard";
import { VoiceList } from "./VoiceList";
import styles from "./NarrationPanel.module.css";

/**
 * NarrationPanel — Phase 25-AB Task 13.
 *
 * Mockup source: docs/mockups/narration.html (panels 22 — 25).
 *
 * Composite of:
 *   - ModeSelector (Mode A 원본 보존 / Mode B 스토리 재구성)
 *   - StoryThemeGrid (12 themes, Mode B only)
 *   - ToneGrid (12 tones, Mode B only)
 *   - ScriptEditor (Mode A only)
 *   - VoiceList (5 voices + Clone slot)
 *   - VoiceCloneWizard (3-step upload → train → use)
 *
 * Coin cost is computed from selected options and surfaced in the
 * summary block + the apply CTA.
 */

export type NarrationConfig = {
	mode: NarrationMode;
	submode: NarrationSubmode;
	theme: string | null;
	tone: string | null;
	voice: string;
	script: string;
	cloneName: string | null;
};

export interface NarrationPanelProps {
	onApply?: (config: NarrationConfig) => void;
}

function computeCoinCost(cfg: {
	mode: NarrationMode;
	scriptLen: number;
	voice: string;
}): number {
	// Mode A (원본 보존) uses less synthesis when A-1 only adds subtitles.
	const base = cfg.mode === "B" ? 2 : 0; // Claude script gen (B only)
	const synth = Math.ceil(cfg.scriptLen / 20); // ~1 C per 20 chars
	const premium = cfg.voice === "hyejin" ? 0 : 0; // no premium tier yet
	return base + synth + premium;
}

export function NarrationPanel({ onApply }: NarrationPanelProps) {
	const router = useRouter();
	const [mode, setMode] = useState<NarrationMode>("B");
	const [submode, setSubmode] = useState<NarrationSubmode>("A-1");
	const [theme, setTheme] = useState<string | null>("storytelling");
	const [tone, setTone] = useState<string | null>("warm");
	const [voice, setVoice] = useState<string>("hyejin");
	// Increment 20 — TTS output language (Worker /narration/synthesize accepts
	// ko/en/ja/zh). Real backend param, not a placeholder.
	const [language, setLanguage] = useState<"ko" | "en" | "ja" | "zh">("ko");
	// Phase 25 — speed (0.5..2.0) + pitch (-12..+12 semitones). Applied
	// server-side via ffmpeg as a post-TTS pass so engine-agnostic.
	const [speed, setSpeed] = useState<number>(1.0);
	const [pitch, setPitch] = useState<number>(0);
	const [script, setScript] = useState<string>("");
	const [cloneOpen, setCloneOpen] = useState(false);
	const [cloneName, setCloneName] = useState<string | null>(null);

	// Increment 5 — real apply flow (Worker /narration/generate + /synthesize).
	const [applying, setApplying] = useState(false);
	const [applyStatus, setApplyStatus] = useState<string | null>(null);
	const [generatedScript, setGeneratedScript] = useState<string>("");
	const [audioUrl, setAudioUrl] = useState<string | null>(null);

	const handleClone = useCallback(() => setCloneOpen(true), []);
	const handleCloneTrained = useCallback((name: string) => {
		setCloneName(name);
		setVoice(VOICE_CLONE_KEY);
	}, []);

	// Approximate generated-script length for cost preview in Mode B.
	const effectiveScriptLen = mode === "A" ? script.length : 240;

	const totalCoins = useMemo(
		() =>
			computeCoinCost({
				mode,
				scriptLen: effectiveScriptLen,
				voice,
			}),
		[mode, effectiveScriptLen, voice],
	);

	const selectedThemeName =
		STORY_THEMES.find((t) => t.key === theme)?.name ?? "—";
	const selectedToneName = TONES.find((t) => t.key === tone)?.name ?? "—";
	const selectedVoiceName =
		voice === VOICE_CLONE_KEY
			? (cloneName ?? "내 목소리")
			: (VOICES.find((v) => v.key === voice)?.name ?? "—");

	const canApply =
		mode === "A"
			? script.trim().length > 0 && !!voice
			: !!theme && !!tone && !!voice;

	/**
	 * Real apply flow:
	 *  - Mode B: Worker `/narration/generate` (Claude script) → `/synthesize`.
	 *  - Mode A: synthesize the user's own script directly.
	 * 401 → /login, 402 → insufficient-credits message, success → audio player.
	 */
	async function handleApply() {
		if (applying) return;
		onApply?.({
			mode,
			submode: mode === "A" ? submode : null,
			theme: mode === "B" ? theme : null,
			tone: mode === "B" ? tone : null,
			voice,
			script,
			cloneName,
		});

		setApplying(true);
		setApplyStatus(null);
		setAudioUrl((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return null;
		});

		let ttsText = script;
		try {
			if (mode === "B") {
				setApplyStatus("스토리 스크립트 생성 중… (Claude)");
				const gen = await generateNarrationScript({ mode: "B", theme, tone });
				if (!gen.ok) {
					if (gen.status === 401) {
						router.push("/login");
						return;
					}
					if (gen.error === "insufficient_credits") {
						setApplyStatus(
							`코인이 부족합니다 (보유 ${gen.balance ?? 0} · 필요 ${gen.required ?? "?"}).`,
						);
						return;
					}
					setApplyStatus(`스크립트 생성 실패: ${gen.error ?? "알 수 없는 오류"}`);
					return;
				}
				ttsText = scriptTextFromResponse(gen.data ?? {});
				setGeneratedScript(ttsText);
			}

			if (!ttsText.trim()) {
				setApplyStatus("합성할 스크립트가 비어 있습니다.");
				return;
			}

			setApplyStatus("TTS 음성 합성 중…");
			const synth = await synthesizeNarration({
				text: ttsText,
				voiceId: voice,
				tone,
				language,
				speed,
				pitch,
			});
			if (!synth.ok) {
				if (synth.status === 401) {
					router.push("/login");
					return;
				}
				if (synth.error === "insufficient_credits") {
					setApplyStatus(
						`코인이 부족합니다 (보유 ${synth.balance ?? 0} · 필요 ${synth.required ?? "?"}).`,
					);
					return;
				}
				setApplyStatus(`음성 합성 실패: ${synth.error ?? "알 수 없는 오류"}`);
				return;
			}
			setAudioUrl(synth.data?.audioUrl ?? null);
			setApplyStatus("나레이션 합성 완료 — 아래에서 미리듣기 하세요.");
		} finally {
			setApplying(false);
		}
	}

	return (
		<div className={styles.nr} data-testid="narration-panel">
			<header className={styles.nrHeader} {...devLabelPanel("22")}>
				<button
					type="button"
					className={styles.hBack}
					aria-label="Editor로 돌아가기"
					{...devLabel("22-A")}
				>
					←
				</button>
				<div className={styles.hTitle} {...devLabel("22-B")}>
					나레이션 <span className={styles.mmlTag}>MML+</span>
				</div>
				<span className={styles.hMeta} {...devLabel("22-C")}>
					{mode === "A"
						? `Mode A · ${submode ?? "A-1"} · ${selectedVoiceName}`
						: `Mode B · ${selectedThemeName} · ${selectedToneName} · ${selectedVoiceName}`}
				</span>
				<span className={styles.hBalance} {...devLabel("22-F")}>
					잔여 273
				</span>
			</header>

			<div className={styles.nrMain}>
				<aside className={styles.nrSide} {...devLabelPanel("23")}>
					<ModeSelector
						mode={mode}
						submode={submode}
						onModeChange={setMode}
						onSubmodeChange={setSubmode}
					/>
				</aside>

				<section className={styles.nrCenter} {...devLabelPanel("24")}>
					<div className={styles.centerBar} {...devLabel("24-A")}>
						<h2 className={styles.centerTitle}>
							{mode === "A" ? "직접 작성" : "스토리 설정"}{" "}
							<span className={styles.centerSub}>
								{mode === "A"
									? "Mode A — 원본 보존"
									: "Mode B — 스토리 재구성"}
							</span>
						</h2>
						{mode === "B" && (
							<span className={styles.modePill}>
								{selectedThemeName} + {selectedToneName} + {selectedVoiceName}
							</span>
						)}
					</div>

					{mode === "B" && (
						<>
							<StoryThemeGrid selected={theme} onSelect={setTheme} />
							<ToneGrid selected={tone} onSelect={setTone} />
						</>
					)}

					{mode === "A" && <ScriptEditor value={script} onChange={setScript} />}
				</section>

				<aside className={styles.nrRight} {...devLabelPanel("25")}>
					<VoiceList
						selected={voice}
						onSelect={setVoice}
						onClone={handleClone}
						cloneTrained={!!cloneName}
					/>

					<div className={styles.rightBlock}>
						<div
							style={{
								fontSize: 11,
								fontWeight: 600,
								opacity: 0.7,
								marginBottom: 6,
							}}
						>
							출력 언어
						</div>
						<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
							{(
								[
									["ko", "한국어"],
									["en", "English"],
									["ja", "日本語"],
									["zh", "中文"],
								] as const
							).map(([code, label]) => (
								<button
									key={code}
									type="button"
									onClick={() => setLanguage(code)}
									data-testid={`narration-lang-${code}`}
									style={{
										padding: "5px 10px",
										borderRadius: 6,
										fontSize: 11,
										fontWeight: 600,
										cursor: "pointer",
										border:
											language === code
												? "1px solid #d4af37"
												: "1px solid rgba(255,255,255,0.15)",
										background:
											language === code
												? "rgba(212,175,55,0.12)"
												: "transparent",
										color: language === code ? "#d4af37" : "#dcdcdc",
									}}
								>
									{label}
								</button>
							))}
						</div>

						<div style={{ marginTop: 14 }}>
							<div
								style={{
									fontSize: 11,
									fontWeight: 600,
									opacity: 0.7,
									marginBottom: 4,
									display: "flex",
									justifyContent: "space-between",
								}}
							>
								<span>속도 (speed)</span>
								<span data-testid="narration-speed-val">{speed.toFixed(2)}x</span>
							</div>
							<input
								type="range"
								min={0.5}
								max={2.0}
								step={0.05}
								value={speed}
								onChange={(e) => setSpeed(parseFloat(e.target.value))}
								data-testid="narration-speed"
								style={{ width: "100%", accentColor: "#d4af37" }}
							/>
						</div>

						<div style={{ marginTop: 10 }}>
							<div
								style={{
									fontSize: 11,
									fontWeight: 600,
									opacity: 0.7,
									marginBottom: 4,
									display: "flex",
									justifyContent: "space-between",
								}}
							>
								<span>피치 (pitch · semitones)</span>
								<span data-testid="narration-pitch-val">
									{pitch > 0 ? "+" : ""}
									{pitch}
								</span>
							</div>
							<input
								type="range"
								min={-12}
								max={12}
								step={1}
								value={pitch}
								onChange={(e) => setPitch(parseInt(e.target.value, 10))}
								data-testid="narration-pitch"
								style={{ width: "100%", accentColor: "#d4af37" }}
							/>
						</div>
					</div>

					<div className={styles.rightBlock} {...devLabel("25-D")}>
						<div className={styles.summary} data-testid="summary">
							<div className={styles.sumRow}>
								<span>모드</span>
								<span className={styles.sumVal}>
									{mode === "A" ? `A · ${submode ?? "A-1"}` : "B"}
								</span>
							</div>
							<div className={styles.sumRow}>
								<span>스크립트 ({effectiveScriptLen}자)</span>
								<span className={styles.sumVal}>
									{mode === "B" ? "+2 C" : "+0 C"}
								</span>
							</div>
							<div className={styles.sumRow}>
								<span>TTS 합성</span>
								<span className={styles.sumVal}>
									+{Math.ceil(effectiveScriptLen / 20)} C
								</span>
							</div>
							<div className={styles.sumRow}>
								<span>Voice</span>
								<span className={styles.sumVal}>{selectedVoiceName}</span>
							</div>
							<div className={`${styles.sumRow} ${styles.sumTotal}`}>
								<span>총 차감</span>
								<span className={styles.sumVal}>{totalCoins} C</span>
							</div>
						</div>
					</div>

					<div className={styles.rightBlock} {...devLabel("25-E")}>
						<button
							type="button"
							className={styles.btnApply}
							onClick={handleApply}
							disabled={!canApply || applying}
							data-testid="apply-narration"
						>
							{applying
								? "처리 중…"
								: `나레이션 적용 (${totalCoins} 코인)`}
						</button>

						{applyStatus && (
							<div
								data-testid="apply-status"
								role="status"
								style={{
									marginTop: 8,
									padding: "8px 10px",
									borderRadius: 6,
									border: "1px solid #d4af37",
									background: "rgba(212,175,55,0.08)",
									color: "#d4af37",
									fontSize: 11,
									lineHeight: 1.5,
								}}
							>
								{applyStatus}
							</div>
						)}

						{generatedScript && (
							<div
								data-testid="generated-script"
								style={{
									marginTop: 8,
									padding: "8px 10px",
									borderRadius: 6,
									border: "1px solid rgba(255,255,255,0.12)",
									background: "rgba(0,0,0,0.3)",
									color: "#dcdcdc",
									fontSize: 11,
									lineHeight: 1.6,
									maxHeight: 160,
									overflowY: "auto",
									whiteSpace: "pre-wrap",
								}}
							>
								{generatedScript}
							</div>
						)}

						{audioUrl && (
							<audio
								data-testid="narration-audio"
								controls
								src={audioUrl}
								style={{ marginTop: 8, width: "100%" }}
							/>
						)}
					</div>
				</aside>
			</div>

			<VoiceCloneWizard
				open={cloneOpen}
				onClose={() => setCloneOpen(false)}
				onTrained={handleCloneTrained}
			/>
		</div>
	);
}
